/**
 * Sensitive Notes Service
 * Handles encryption, decryption, and local storage of sensitive notes
 * Works entirely on the frontend without backend API calls
 */

import { encryptNote, decryptNote, isCryptoSupported } from '../utils/crypto'

class SensitiveNotesService {
  constructor() {
    this.STORAGE_KEY = 'sensitive_notes'
    this.INDEX_KEY = 'sensitive_notes_index'
  }

  /**
   * Check if crypto is supported
   */
  isSupported() {
    return isCryptoSupported()
  }

  /**
   * Detect which encryption method was used for a note
   */
  detectEncryptionMethod(encryptedNote) {
    // Check if the note has encryptedAt timestamp (indicates crypto.js encryption)
    if (encryptedNote.encryptedAt) {
      return 'crypto.js'
    }
    
    // Check if title/content look like base64 (LocalStorageService XOR encryption)
    const isBase64 = (str) => {
      if (!str || typeof str !== 'string') return false
      try {
        return btoa(atob(str)) === str
      } catch {
        return false
      }
    }
    
    if (isBase64(encryptedNote.title) || isBase64(encryptedNote.content)) {
      return 'localStorageService'
    }
    
    // Default to crypto.js for notes without clear indicators
    return 'crypto.js'
  }

  /**
   * Get encrypted notes from localStorage
   */
  getEncryptedNotes() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to load encrypted notes:', error)
      return {}
    }
  }

  /**
   * Save encrypted notes to localStorage
   */
  saveEncryptedNotes(notes) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notes))
      this.updateIndex(notes)
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to save encrypted notes:', error)
      throw error
    }
  }

  /**
   * Update search index for encrypted notes (stores metadata only, not content)
   */
  updateIndex(notes) {
    try {
      const index = Object.keys(notes).map(noteId => {
        const note = notes[noteId]
        return {
          noteId,
          folderId: note.folderId,
          createdAt: note.createdAt,
          lastModified: note.lastModified,
          isEncrypted: true
        }
      })
      localStorage.setItem(this.INDEX_KEY, JSON.stringify(index))
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to update index:', error)
    }
  }

  /**
   * Get notes index (for listing without decryption)
   */
  getNotesIndex() {
    try {
      const stored = localStorage.getItem(this.INDEX_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to load notes index:', error)
      return []
    }
  }

  /**
   * Migrate a note from old encryption system to new system
   */
  async migrateNote(noteData, password) {
    try {
      if (!this.isSupported()) {
        throw new Error('Crypto not supported in this browser')
      }

      console.log('🔄 SensitiveNotes: Migrating note:', noteData.title)

      // First, decrypt the note using the old system
      const { default: localStorageService } = await import('./localStorageService')
      let decryptedNote = { ...noteData }

      // Decrypt each field if it's encrypted
      if (noteData.title && noteData.isEncrypted) {
        try {
          // Check if title looks like it's encrypted (base64-like)
          if (typeof noteData.title === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(noteData.title)) {
            decryptedNote.title = await localStorageService.decryptContent(noteData.title, password)
          } else {
            console.log('🔍 SensitiveNotes: Title does not appear to be encrypted, keeping as-is')
          }
        } catch (error) {
          console.log('🔍 SensitiveNotes: Title not encrypted or already decrypted:', error.message)
        }
      }

      if (noteData.content && noteData.isEncrypted) {
        try {
          // Check if content looks like it's encrypted (base64-like)
          if (typeof noteData.content === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(noteData.content)) {
            decryptedNote.content = await localStorageService.decryptContent(noteData.content, password)
          } else {
            console.log('🔍 SensitiveNotes: Content does not appear to be encrypted, keeping as-is')
          }
        } catch (error) {
          console.log('🔍 SensitiveNotes: Content not encrypted or already decrypted:', error.message)
        }
      }

      if (noteData.tags && noteData.isEncrypted) {
        try {
          // Check if tags look like they're encrypted (base64-like)
          if (typeof noteData.tags === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(noteData.tags)) {
            decryptedNote.tags = await localStorageService.decryptContent(noteData.tags, password)
          } else {
            console.log('🔍 SensitiveNotes: Tags do not appear to be encrypted, keeping as-is')
          }
        } catch (error) {
          console.log('🔍 SensitiveNotes: Tags not encrypted or already decrypted:', error.message)
        }
      }

      // Mark as decrypted
      decryptedNote.isEncrypted = false
      delete decryptedNote.encryptedAt

      console.log('✅ SensitiveNotes: Note decrypted from old system')

      // If the note is already in plain text, just encrypt it with the new system
      if (decryptedNote.title && typeof decryptedNote.title === 'string' && 
          decryptedNote.content && typeof decryptedNote.content === 'string') {
        return await this.createNote(decryptedNote, password)
      } else {
        throw new Error('Note data is invalid after decryption attempt')
      }

    } catch (error) {
      console.error('❌ SensitiveNotes: Migration failed:', error)
      throw error
    }
  }

  /**
   * Create a new sensitive note
   */
  async createNote(noteData, password) {
    try {
      if (!this.isSupported()) {
        throw new Error('Crypto not supported in this browser')
      }

      console.log('🔐 SensitiveNotes: Creating encrypted note:', typeof noteData.title === 'string' ? noteData.title : 'Unknown')

      // Generate unique ID if not provided
      const noteId = noteData.noteId || `sensitive-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      
      // Prepare note for encryption
      const noteToEncrypt = {
        ...noteData,
        noteId,
        id: noteId,
        createdAt: noteData.createdAt || new Date().toISOString(),
        lastModified: new Date().toISOString(),
        isSensitive: true
      }

      // Check if the note is already encrypted (from EditNotePage or LocalStorageService)
      let encryptedNote
      if (noteData.isEncrypted && noteData.encryptedAt) {
        console.log('🔐 SensitiveNotes: Note already encrypted, storing as-is')
        // Note is already encrypted, store it directly
        encryptedNote = {
          ...noteToEncrypt,
          isEncrypted: true,
          encryptedAt: noteData.encryptedAt
        }
      } else if (noteData.isEncrypted && !noteData.encryptedAt) {
        console.log('🔐 SensitiveNotes: Note content is encrypted but not the full note, storing as-is')
        // Note content is encrypted but not the full note object (from EditNotePage)
        encryptedNote = {
          ...noteToEncrypt,
          isEncrypted: true,
          encryptedAt: new Date().toISOString()
        }
      } else {
        console.log('🔐 SensitiveNotes: Encrypting note for first time')
        // Encrypt the note
        encryptedNote = await encryptNote(noteToEncrypt, password)
      }

      // Load existing notes
      const existingNotes = this.getEncryptedNotes()

      // Add new encrypted note
      existingNotes[noteId] = encryptedNote

      // Save to localStorage
      this.saveEncryptedNotes(existingNotes)

      console.log('✅ SensitiveNotes: Note encrypted and saved locally')

      // Return the original note (unencrypted) for immediate UI use
      return noteToEncrypt

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to create sensitive note:', error)
      throw error
    }
  }

  /**
   * Get a specific sensitive note by ID
   */
  async getNote(noteId, password) {
    try {
      if (!this.isSupported()) {
        throw new Error('Crypto not supported in this browser')
      }

      const encryptedNotes = this.getEncryptedNotes()
      const encryptedNote = encryptedNotes[noteId]

      if (!encryptedNote) {
        throw new Error('Note not found')
      }

      // Debug: Log the encrypted note structure
      console.log('🔍 SensitiveNotes: Encrypted note structure:', {
        noteId: encryptedNote.noteId,
        title: encryptedNote.title,
        content: encryptedNote.content?.substring(0, 50),
        isEncrypted: encryptedNote.isEncrypted,
        encryptedAt: encryptedNote.encryptedAt
      })
      console.log('🔍 SensitiveNotes: Password details:', {
        passwordLength: password?.length,
        passwordPreview: password?.substring(0, 3) + '...',
        passwordHash: password ? btoa(password).substring(0, 10) + '...' : 'none'
      })
      
      // Check if this note was encrypted by LocalStorageService (XOR + base64) or crypto.js
      // LocalStorageService encrypted notes have a different structure - they don't have encryptedAt
      if (encryptedNote.isEncrypted && encryptedNote.encryptedAt) {
        // If the note has encryptedAt, it was encrypted with the new AES-GCM system
        // Check if the title looks like it's encrypted (base64-like with padding)
        const titleLooksEncrypted = encryptedNote.title && (
          encryptedNote.title.includes('=') || 
          encryptedNote.title.length > 50 || 
          /^[A-Za-z0-9+/]*={0,2}$/.test(encryptedNote.title)
        )
        
        if (titleLooksEncrypted) {
          // Entire note is encrypted (from SensitiveNotesService.createNote)
          console.log('🔐 SensitiveNotes: Using crypto.js decryption for full note')
          try {
            const decryptedNote = await decryptNote(encryptedNote, password)
            console.log('🔍 SensitiveNotes: Decrypted note from crypto.js:', decryptedNote)
            console.log('🔍 SensitiveNotes: Content property exists:', 'content' in decryptedNote)
            console.log('🔍 SensitiveNotes: Content value:', decryptedNote.content)
            return decryptedNote
          } catch (cryptoError) {
            console.error('❌ SensitiveNotes: Crypto.js decryption failed:', cryptoError.message)
            throw new Error('Failed to decrypt note. The password may be incorrect or the note was encrypted with a different method.')
          }
        } else {
          // Title is not encrypted, only content is encrypted (from EditNotePage)
          console.log('🔐 SensitiveNotes: Only content is encrypted, decrypting content only')
          const { decryptText } = await import('../utils/crypto')
          const decryptedNote = { ...encryptedNote }
          decryptedNote.content = await decryptText(encryptedNote.content, password)
          decryptedNote.isEncrypted = false
          delete decryptedNote.encryptedAt
          console.log('✅ SensitiveNotes: Content-only decryption successful')
          return decryptedNote
        }
      } else {
        // This might be a LocalStorageService encrypted note, try to decrypt with XOR method
        console.log('🔐 SensitiveNotes: Attempting LocalStorageService decryption')
        try {
          // Import LocalStorageService to use its decryption method
          const { default: localStorageService } = await import('./localStorageService')
          
          // Create a decrypted version using LocalStorageService method
          const decryptedNote = { ...encryptedNote }
          
          // Decrypt title and content using LocalStorageService method
          if (encryptedNote.title) {
            decryptedNote.title = localStorageService.decryptContent(encryptedNote.title, password)
          }
          if (encryptedNote.content) {
            decryptedNote.content = localStorageService.decryptContent(encryptedNote.content, password)
          }
          if (encryptedNote.tags) {
            decryptedNote.tags = localStorageService.decryptContent(encryptedNote.tags, password)
          }
          
          // Mark as decrypted
          decryptedNote.isEncrypted = false
          delete decryptedNote.encryptedAt
          
          console.log('✅ SensitiveNotes: LocalStorageService decryption successful')
          return decryptedNote
        } catch (xorError) {
          console.error('❌ SensitiveNotes: LocalStorageService decryption failed:', xorError.message)
          throw new Error('Failed to decrypt note. Please check your password.')
        }
      }

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to get sensitive note:', error)
      throw error
    }
  }

  /**
   * Get all sensitive notes for a folder
   */
  async getNotes(folderId, password) {
    try {
      if (!this.isSupported()) {
        throw new Error('Crypto not supported in this browser')
      }

      console.log('🔐 SensitiveNotes: Getting sensitive notes for folder:', folderId)

      const encryptedNotes = this.getEncryptedNotes()
      const decryptedNotes = []

      // Decrypt all notes for the folder
      for (const [noteId, encryptedNote] of Object.entries(encryptedNotes)) {
        if (encryptedNote.folderId === folderId) {
          try {
            const decryptedNote = await decryptNote(encryptedNote, password)
            decryptedNotes.push(decryptedNote)
          } catch (error) {
            console.error(`❌ SensitiveNotes: Failed to decrypt note ${noteId}:`, error)
            // Skip notes that can't be decrypted (wrong password)
          }
        }
      }

      // Sort by lastModified (newest first)
      decryptedNotes.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))

      console.log(`✅ SensitiveNotes: Decrypted ${decryptedNotes.length} notes`)
      return decryptedNotes

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to get sensitive notes:', error)
      throw error
    }
  }

  /**
   * Get encrypted sensitive notes for a folder (for list display)
   * Returns notes with encrypted content - no decryption
   */
  async getEncryptedNotesForFolder(folderId) {
    try {
      console.log('🔐 SensitiveNotes: Getting encrypted notes for folder:', folderId)

      const encryptedNotes = this.getEncryptedNotes()
      const folderNotes = []

      // Get all notes for the folder (encrypted)
      for (const [noteId, encryptedNote] of Object.entries(encryptedNotes)) {
        if (encryptedNote.folderId === folderId) {
          // Return encrypted note with placeholder content for list display
          const listNote = {
            ...encryptedNote,
            content: '🔒 This note is encrypted. Please authenticate to view its content.',
            isEncrypted: true
          }
          folderNotes.push(listNote)
        }
      }

      // Sort by lastModified (newest first)
      folderNotes.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))

      console.log(`✅ SensitiveNotes: Found ${folderNotes.length} encrypted notes for folder`)
      return folderNotes

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to get encrypted notes for folder:', error)
      throw error
    }
  }

  /**
   * Update a sensitive note
   */
  async updateNote(noteId, noteData, password) {
    try {
      if (!this.isSupported()) {
        throw new Error('Crypto not supported in this browser')
      }

      console.log('🔐 SensitiveNotes: Updating encrypted note:', noteId)

      // Load existing notes
      const existingNotes = this.getEncryptedNotes()
      
      if (!existingNotes[noteId]) {
        throw new Error('Note not found')
      }

      // Prepare updated note
      const updatedNote = {
        ...noteData,
        noteId,
        id: noteId,
        lastModified: new Date().toISOString(),
        isSensitive: true
      }

      // Encrypt the updated note
      const encryptedNote = await encryptNote(updatedNote, password)

      // Update in storage
      existingNotes[noteId] = encryptedNote
      this.saveEncryptedNotes(existingNotes)

      console.log('✅ SensitiveNotes: Note updated and re-encrypted')
      return updatedNote

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to update sensitive note:', error)
      throw error
    }
  }

  /**
   * Delete a sensitive note
   */
  async deleteNote(noteId) {
    try {
      console.log('🔐 SensitiveNotes: Deleting encrypted note:', noteId)

      const existingNotes = this.getEncryptedNotes()
      
      if (!existingNotes[noteId]) {
        throw new Error('Note not found')
      }

      // Remove from storage
      delete existingNotes[noteId]
      this.saveEncryptedNotes(existingNotes)

      console.log('✅ SensitiveNotes: Note deleted from encrypted storage')
      return { success: true, noteId }

    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to delete sensitive note:', error)
      throw error
    }
  }

  /**
   * Search sensitive notes (requires decryption)
   */
  async searchNotes(query, folderId, password) {
    try {
      if (!query || query.trim().length === 0) {
        return await this.getNotes(folderId, password)
      }

      console.log('🔍 SensitiveNotes: Searching encrypted notes for:', query)

      const allNotes = await this.getNotes(folderId, password)
      const searchTerm = query.toLowerCase()

      // Search in decrypted content
      const matches = allNotes.filter(note => {
        return (
          (note.title && note.title.toLowerCase().includes(searchTerm)) ||
          (note.content && note.content.toLowerCase().includes(searchTerm)) ||
          (note.tags && Array.isArray(note.tags) && 
           note.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
        )
      })

      console.log(`✅ SensitiveNotes: Found ${matches.length} matching notes`)
      return matches

    } catch (error) {
      console.error('❌ SensitiveNotes: Search failed:', error)
      throw error
    }
  }

  /**
   * Get storage statistics
   */
  getStorageStats() {
    try {
      const encryptedNotes = this.getEncryptedNotes()
      const index = this.getNotesIndex()
      
      return {
        totalNotes: Object.keys(encryptedNotes).length,
        indexedNotes: index.length,
        storageSize: JSON.stringify(encryptedNotes).length,
        isSupported: this.isSupported()
      }
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to get storage stats:', error)
      return {
        totalNotes: 0,
        indexedNotes: 0,
        storageSize: 0,
        isSupported: this.isSupported()
      }
    }
  }

  /**
   * Clear all sensitive notes (for testing/cleanup)
   */
  clearAll() {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      localStorage.removeItem(this.INDEX_KEY)
      console.log('✅ SensitiveNotes: All encrypted notes cleared')
    } catch (error) {
      console.error('❌ SensitiveNotes: Failed to clear notes:', error)
    }
  }
}

// Export singleton instance
export const sensitiveNotesService = new SensitiveNotesService()
export default sensitiveNotesService
